import { zodResolver } from '@hookform/resolvers/zod';
import { EquipmentCreate, type EquipmentPatch, type Equipment } from '@siteday/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import type { z } from 'zod';

import { equipmentApi, errorMessage, qk } from '@/api';
import {
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
} from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Textarea } from '@/components/ui/input';
import { Card, CardBody, CardHeader, Table, Th } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { CheckboxChip, Switch } from '@/components/ui/toggle';
import { useQboCandidates, useQboStatus } from '@/features/jobs/JobForm';
import { cn } from '@/lib/cn';
import { formatRate } from '@/lib/format';
import { sortRows, useTableSort } from '@/lib/sort';
import { useUrlState } from '@/lib/url-state';

const DEFAULTS = { active: '1', sort: '', paste: '' };

/** Bulk paste parser: name, type, unit #, hourly cost — tab or comma separated, one machine per line. */
export function parseEquipmentPaste(
  text: string,
): Array<{
  name: string;
  type: string | null;
  unitNumber: string | null;
  defaultHourlyCost: string;
  error?: string;
}> {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const cells = (line.includes('\t') ? line.split('\t') : line.split(',')).map((c) => c.trim());
      const [name = '', type = '', unitNumber = '', cost = '0'] = cells;
      const costClean = cost.replace(/[$,\s]/g, '') || '0';
      const row = {
        name,
        type: type || null,
        unitNumber: unitNumber || null,
        defaultHourlyCost: costClean,
      };
      if (!name) return { ...row, error: 'missing name' };
      if (!/^\d+(\.\d+)?$/.test(costClean)) return { ...row, error: `bad cost "${cost}"` };
      return row;
    });
}

function useInvalidateEquipment() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: qk.equipment.all() });
}

/** W8 — fleet table, add/edit dialog, bulk paste from a spreadsheet (textarea → preview → create). */
export function EquipmentPage() {
  const [state, setState] = useUrlState(DEFAULTS);
  const list = useQuery({
    queryKey: qk.equipment.list({}),
    queryFn: () => equipmentApi.listEquipment({}),
  });
  const qbo = useQboStatus();
  const candidates = useQboCandidates(qbo.data?.connected === true);
  const sort = useTableSort(state.sort, (v) => setState({ sort: v }));
  const invalidate = useInvalidateEquipment();
  const toast = useToast();
  const [editing, setEditing] = useState<Equipment | null | 'new'>(null);
  const [pasteOpen, setPasteOpen] = useState(state.paste === '1');

  const patch = useMutation({
    mutationFn: ({ id, data }: { id: string; data: z.input<typeof EquipmentPatch> }) =>
      equipmentApi.patchEquipment(id, data),
    onSuccess: invalidate,
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = useMemo(
    () =>
      sortRows(
        (list.data?.items ?? []).filter((e) => state.active !== '1' || e.active),
        sort.column,
        sort.dir,
        {
          name: (e) => e.name,
          type: (e) => e.type,
          unit: (e) => e.unitNumber,
          cost: (e) => e.defaultHourlyCost,
          active: (e) => (e.active ? 1 : 0),
        },
      ),
    [list.data, state.active, sort.column, sort.dir],
  );
  // Q-0 2/5: a machine maps to a QuickBooks Vendor (set on W10 Equipment ↔ Vendors); `qboItemId` is the unused doc 04 column.
  const vendorName = (id: string | null) =>
    id ? (candidates.data?.vendors.find((v) => v.id === id)?.name ?? id) : null;

  return (
    <>
      <PageHeader
        title="Equipment"
        sub={list.data ? `${list.data.items.filter((e) => e.active).length} machines` : undefined}
        actions={
          <>
            <Button onClick={() => setPasteOpen((o) => !o)}>Paste from spreadsheet</Button>
            <Button variant="primary" onClick={() => setEditing('new')}>
              <Plus /> Add machine
            </Button>
          </>
        }
      />
      <FilterBar>
        <CheckboxChip
          label="Active only"
          checked={state.active === '1'}
          onChange={(v) => setState({ active: v ? '1' : '0' })}
        />
      </FilterBar>
      <div className={cn(pasteOpen && 'three')}>
        {list.isPending ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <Card>
            <Table>
              <thead>
                <tr>
                  <Th sortKey="name" sort={sort}>
                    Name
                  </Th>
                  <Th sortKey="type" sort={sort}>
                    Type
                  </Th>
                  <Th sortKey="unit" sort={sort}>
                    Unit #
                  </Th>
                  <Th right sortKey="cost" sort={sort}>
                    Hourly cost
                  </Th>
                  <Th sortKey="active" sort={sort}>
                    Active
                  </Th>
                  <Th>
                    QuickBooks vendor{' '}
                    <Link
                      to="/quickbooks?tab=equipment"
                      className="ml-1 inline-flex items-center gap-0.5 font-normal text-orange-ink hover:underline"
                      data-testid="equipment-vendor-mappings"
                    >
                      mappings <ExternalLink className="size-3" />
                    </Link>
                  </Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState>
                        No machines yet. Add one or paste your fleet from a spreadsheet.
                      </EmptyState>
                    </td>
                  </tr>
                ) : null}
                {rows.map((e) => (
                  <tr
                    key={e.id}
                    className={cn(!e.active && 'text-ink3')}
                    data-testid="equipment-row"
                  >
                    <td>{e.name}</td>
                    <td>{e.type ?? '—'}</td>
                    <td className="num">{e.unitNumber ?? '—'}</td>
                    <td className="r">{formatRate(e.defaultHourlyCost)}</td>
                    <td>
                      <Switch
                        aria-label={`${e.name} active`}
                        checked={e.active}
                        onChange={(v) => patch.mutate({ id: e.id, data: { active: v } })}
                      />
                    </td>
                    <td className={e.qboVendorId ? undefined : 'text-ink3'} data-testid="equipment-vendor">
                      {vendorName(e.qboVendorId) ?? 'not mapped'}
                    </td>
                    <td className="act">
                      <Button size="sm" onClick={() => setEditing(e)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
        {pasteOpen ? <PasteCard onClose={() => setPasteOpen(false)} /> : null}
      </div>
      {editing ? (
        <EquipmentDialog
          equipment={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function PasteCard({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState('');
  const [step, setStep] = useState<1 | 2>(1);
  const invalidate = useInvalidateEquipment();
  const toast = useToast();
  const parsed = useMemo(() => parseEquipmentPaste(text), [text]);
  const valid = parsed.filter((r) => !r.error);
  const create = useMutation({
    mutationFn: async () => {
      for (const r of valid)
        await equipmentApi.createEquipment({
          name: r.name,
          type: r.type,
          unitNumber: r.unitNumber,
          defaultHourlyCost: r.defaultHourlyCost,
        });
      return valid.length;
    },
    onSuccess: (n) => {
      invalidate();
      toast.success(`${n} machines created`);
      setText('');
      setStep(1);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card className="self-start">
      <CardHeader
        title="Paste from spreadsheet"
        right={
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        }
      />
      <CardBody className="flex flex-col gap-3">
        <p className="m-0 text-xs text-ink2">
          Columns: name, type, unit #, hourly cost. Tab or comma separated, one machine per line.
        </p>
        <Textarea
          aria-label="Paste rows"
          rows={5}
          className="font-mono text-xs whitespace-pre"
          placeholder={
            'Volvo A25 Haul Truck\tTruck\tH-801\t70\nWacker Trench Roller\tCompactor\tR-502\t30'
          }
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setStep(1);
          }}
        />
        <div className="flex items-stretch overflow-hidden rounded-[10px] border border-line2 bg-surface">
          {[
            ['Paste', text.trim().length > 0],
            ['Preview', step === 2],
            ['Create', false],
          ].map(([label, done], i) => (
            <div
              key={String(label)}
              className={cn(
                'flex flex-1 items-center gap-2 border-r border-line2 px-3 py-2 font-semibold text-ink3 last:border-r-0',
                (i === 0 && text ? true : i === 1 && step === 2) && 'bg-hi text-ink',
              )}
            >
              <i
                className={cn(
                  'inline-flex size-5 items-center justify-center rounded-full bg-bg2 text-[11px] font-extrabold not-italic',
                  done && i === 0 && 'bg-good text-on-status',
                  i === 1 && step === 2 && 'bg-orange text-on-orange',
                )}
              >
                {done && i === 0 ? <Check className="size-3" /> : i + 1}
              </i>
              {label}
            </div>
          ))}
        </div>
        {step === 1 ? (
          <Button variant="primary" disabled={parsed.length === 0} onClick={() => setStep(2)}>
            Preview {parsed.length ? `${parsed.length} rows` : ''}
          </Button>
        ) : (
          <>
            <Table className="text-xs">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Unit #</th>
                  <th className="r">$/h</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map((r, i) => (
                  <tr key={i} className={cn(r.error && 'text-bad')}>
                    <td>
                      {r.name || '—'}
                      {r.error ? <span className="sub text-bad">{r.error}</span> : null}
                    </td>
                    <td>{r.type ?? '—'}</td>
                    <td>{r.unitNumber ?? '—'}</td>
                    <td className="r">{r.error ? '—' : formatRate(r.defaultHourlyCost)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Button
              variant="primary"
              disabled={valid.length === 0}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Create {valid.length} machine{valid.length === 1 ? '' : 's'}
            </Button>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function EquipmentDialog({
  equipment,
  onClose,
}: {
  equipment: Equipment | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidateEquipment();
  // EquipmentPatch is EquipmentCreate.partial(); validating the full shape on edit keeps the same rules.
  const form = useForm<z.input<typeof EquipmentCreate>, unknown, z.output<typeof EquipmentCreate>>({
    resolver: zodResolver(EquipmentCreate),
    defaultValues: {
      name: equipment?.name ?? '',
      type: equipment?.type ?? '',
      unitNumber: equipment?.unitNumber ?? '',
      defaultHourlyCost: equipment?.defaultHourlyCost ?? '0',
    },
  });
  const qbo = useQboStatus();
  const candidates = useQboCandidates(qbo.data?.connected === true);
  const vendor = equipment?.qboVendorId
    ? (candidates.data?.vendors.find((v) => v.id === equipment.qboVendorId)?.name ?? equipment.qboVendorId)
    : null;
  const save = useMutation({
    mutationFn: (v: z.output<typeof EquipmentCreate>) => {
      const data = {
        ...v,
        type: v.type?.trim() ? v.type.trim() : null,
        unitNumber: v.unitNumber?.trim() ? v.unitNumber.trim() : null,
      };
      return equipment
        ? equipmentApi.patchEquipment(equipment.id, data)
        : equipmentApi.createEquipment(data);
    },
    onSuccess: (e) => {
      invalidate();
      toast.success(equipment ? 'Saved' : `${e.name} added`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const err = form.formState.errors;
  return (
    <Dialog
      open
      onClose={onClose}
      title={equipment ? `Edit ${equipment.name}` : 'Add machine'}
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="equipment-form" loading={save.isPending}>
            {equipment ? 'Save' : 'Add machine'}
          </Button>
        </>
      }
    >
      <form
        id="equipment-form"
        noValidate
        className="form-grid"
        onSubmit={form.handleSubmit((v) => save.mutate(v))}
      >
        <Field label="Name" htmlFor="eq-name" error={err.name?.message} wide>
          <Input
            id="eq-name"
            autoFocus
            placeholder="CAT 320 Excavator"
            invalid={!!err.name}
            {...form.register('name')}
          />
        </Field>
        <Field label="Type" htmlFor="eq-type" error={err.type?.message}>
          <Input id="eq-type" placeholder="Excavator" {...form.register('type')} />
        </Field>
        <Field label="Unit #" htmlFor="eq-unit" error={err.unitNumber?.message}>
          <Input id="eq-unit" placeholder="E-101" {...form.register('unitNumber')} />
        </Field>
        <Field
          label="Hourly cost"
          htmlFor="eq-cost"
          error={err.defaultHourlyCost?.message}
          hint="Used for the machine cost estimate"
        >
          <Input
            id="eq-cost"
            inputMode="decimal"
            invalid={!!err.defaultHourlyCost}
            {...form.register('defaultHourlyCost')}
          />
        </Field>
        {equipment ? (
          <Field
            label="QuickBooks vendor"
            htmlFor="eq-vendor"
            hint="Read-only here — the mapping is set on the QuickBooks page (Equipment ↔ Vendors)."
            wide
          >
            <div
              id="eq-vendor"
              className="flex items-center justify-between gap-2 rounded-lg border border-line2 bg-bg2 px-3 py-2 text-[13px]"
              data-testid="equipment-dialog-vendor"
            >
              <span className={vendor ? undefined : 'text-ink3'}>{vendor ?? 'Not mapped'}</span>
              <Link
                to="/quickbooks?tab=equipment"
                className="inline-flex items-center gap-1 font-semibold text-orange-ink hover:underline"
              >
                Map in QuickBooks <ExternalLink className="size-3" />
              </Link>
            </div>
          </Field>
        ) : null}
      </form>
    </Dialog>
  );
}
